import { Lock, Search } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { Callout } from '@/components/patterns/callout';
import { EstadoErro, EstadoVazio } from '@/components/patterns/states';
import { ehColaborador } from '@/lib/colaborador';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, Input } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { dataSource } from '@/data';
import { mensagemDoErro } from '@/data/http';
import type { UsuarioComCapabilities, UsuarioKeycloak } from '@/data/types';
import { useBuscaRemota } from '@/hooks/use-busca-remota';
import { useResource } from '@/hooks/use-resource';
import {
  alternarCapability,
  atualizarCapabilityNaLista,
  CAPABILITIES_GERENCIAVEIS,
  nomeExibido,
  rotuloDaCapability,
} from '@/lib/admin-capabilities';
import { CAPABILITY_SYSADMIN } from '@/lib/capabilities';

/**
 * Aba "Permissões" da página Usuários (espelho da do PAN): quem já tem
 * capability, busca de usuário do Keycloak e um Switch por capability com
 * estado otimista + rollback.
 */

/** Linhas fantasma com a altura de uma linha real (nome + detalhe, controle à direita). */
function LinhasCarregando({ linhas = 4, rotulo = 'Carregando' }: { linhas?: number; rotulo?: string }) {
  return (
    <div className="flex flex-col gap-3 p-3" role="status" aria-label={rotulo}>
      {Array.from({ length: linhas }).map((_, indice) => (
        <div key={indice} className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-36" />
            <Skeleton className="h-2.5 w-48 max-w-full" />
          </div>
          <Skeleton className="h-5 w-9 shrink-0 rounded-full" />
        </div>
      ))}
    </div>
  );
}

function LinhaUsuario({
  usuario,
  capabilities,
  selecionado,
  aoSelecionar,
}: {
  usuario: UsuarioKeycloak;
  capabilities: string[];
  selecionado: boolean;
  aoSelecionar: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        aria-pressed={selecionado}
        onClick={aoSelecionar}
        className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1.5 px-3 py-2.5 text-left transition-colors duration-fast ease-standard hover:bg-bg-subtle aria-pressed:bg-primary-wash"
      >
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-cell font-bold">{nomeExibido(usuario)}</span>
          <span className="truncate text-micro font-normal text-on-surface-muted">{usuario.email}</span>
        </span>
        {!ehColaborador(usuario.email) ? (
          <Badge
            tone="warning"
            title="Capability só vale para contas @nasajon.com.br: esta conta de fora não tem efeito nenhum. Revogue."
          >
            Conta externa · sem efeito
          </Badge>
        ) : null}
        {capabilities.length > 0 ? (
          <span className="flex flex-wrap gap-1">
            {capabilities.map((capability) => (
              <Badge key={capability} tone="primary">
                {rotuloDaCapability(capability)}
              </Badge>
            ))}
          </span>
        ) : null}
      </button>
    </li>
  );
}

export function PainelCapabilities({ ehSysadmin }: { ehSysadmin: boolean }) {
  const lista = useResource<UsuarioComCapabilities[]>((signal) => dataSource.listarUsuariosComCapabilities(signal));
  const busca = useBuscaRemota<UsuarioKeycloak>((termo, signal) => dataSource.buscarUsuariosKeycloak(termo, signal));
  const [selecionado, setSelecionado] = React.useState<UsuarioKeycloak | null>(null);
  const [emAcao, setEmAcao] = React.useState<ReadonlySet<string>>(new Set());

  const idSelecionado = selecionado?.keycloak_user_id ?? null;
  const capsDoUsuario = useResource<string[]>(
    async (signal) =>
      idSelecionado ? (await dataSource.obterCapabilitiesDoUsuario(idSelecionado, signal)).capabilities : [],
    [idSelecionado],
  );

  // `sysadmin` só aparece para quem é sysadmin (a API já omite; aqui também, p.ex. no modo demo). Quem só tinha essa
  // capability some da lista.
  const gerenciaveis = React.useMemo(
    () => CAPABILITIES_GERENCIAVEIS.filter((item) => item.valor !== CAPABILITY_SYSADMIN || ehSysadmin),
    [ehSysadmin],
  );
  const usuariosVisiveis = React.useMemo(
    () =>
      (lista.dados ?? [])
        .map((item) => ({
          ...item,
          capabilities: ehSysadmin ? item.capabilities : item.capabilities.filter((c) => c !== CAPABILITY_SYSADMIN),
        }))
        .filter((item) => item.capabilities.length > 0),
    [lista.dados, ehSysadmin],
  );
  const capsPorUsuario = React.useMemo(
    () => new Map(usuariosVisiveis.map((item) => [item.keycloak_user_id, item.capabilities])),
    [usuariosVisiveis],
  );

  // Refs para o handler otimista sempre enxergar o estado mais novo, sem
  // depender do closure do clique (dois cliques rápidos em capabilities
  // diferentes não podem desfazer um ao outro no rollback).
  const listaRef = React.useRef(lista);
  listaRef.current = lista;
  const capsRef = React.useRef(capsDoUsuario);
  capsRef.current = capsDoUsuario;
  const idSelecionadoRef = React.useRef(idSelecionado);
  idSelecionadoRef.current = idSelecionado;

  function selecionar(usuario: UsuarioKeycloak) {
    setSelecionado(usuario);
    busca.setTermo('');
  }

  async function alternar(capability: string, concedida: boolean) {
    if (!selecionado) return;
    const usuario = selecionado;
    const incluir = !concedida;
    const chave = `${usuario.keycloak_user_id}:${capability}`;

    const aplicar = (valor: boolean) => {
      // O painel da direita só reflete o usuário que ainda está selecionado.
      if (idSelecionadoRef.current === usuario.keycloak_user_id && capsRef.current.dados) {
        capsRef.current.definir(alternarCapability(capsRef.current.dados, capability, valor));
      }
      if (listaRef.current.dados) {
        listaRef.current.definir(atualizarCapabilityNaLista(listaRef.current.dados, usuario, capability, valor));
      }
    };

    setEmAcao((atual) => new Set(atual).add(chave));
    aplicar(incluir); // otimista: só esta capability muda, na hora e sem skeleton
    try {
      if (concedida) await dataSource.revogarCapability(usuario.keycloak_user_id, capability);
      else await dataSource.concederCapability(usuario.keycloak_user_id, capability);
    } catch (causa) {
      aplicar(concedida);
      toast.error(
        `Não foi possível ${incluir ? 'conceder' : 'revogar'} "${capability}": ${
          mensagemDoErro(causa) ?? (causa instanceof Error ? causa.message : String(causa))
        }`,
      );
    } finally {
      setEmAcao((atual) => {
        const proximo = new Set(atual);
        proximo.delete(chave);
        return proximo;
      });
    }
  }

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
      <Card>
        <CardHeader>
          <CardTitle>Usuários</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Field
            id="busca-usuario-capability"
            label="Buscar usuário"
            apoio="Digite ao menos 2 caracteres do nome, username ou e-mail (@nasajon.com.br) para adicionar alguém."
          >
            {(props) => (
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-icon"
                  aria-hidden="true"
                />
                <Input
                  {...props}
                  type="search"
                  autoComplete="off"
                  value={busca.termo}
                  placeholder="Ex.: ana.souza"
                  onChange={(evento) => busca.setTermo(evento.target.value)}
                  className="pl-9"
                />
              </div>
            )}
          </Field>

          {busca.ativa ? (
            <section aria-label="Resultado da busca" className="rounded-input border border-border">
              {busca.carregando ? (
                <LinhasCarregando linhas={3} rotulo="Buscando usuários" />
              ) : busca.erro ? (
                <div className="p-2">
                  <Callout
                    tom="erro"
                    acao={
                      <button
                        type="button"
                        onClick={busca.tentarDeNovo}
                        className="text-label font-bold text-primary underline"
                      >
                        Tentar de novo
                      </button>
                    }
                  >
                    {busca.erro}
                  </Callout>
                </div>
              ) : busca.resultados.length > 0 ? (
                <ul aria-label="Usuários encontrados" className="divide-y divide-border-subtle">
                  {busca.resultados.map((usuario) => (
                    <LinhaUsuario
                      key={usuario.keycloak_user_id}
                      usuario={usuario}
                      capabilities={capsPorUsuario.get(usuario.keycloak_user_id) ?? []}
                      selecionado={idSelecionado === usuario.keycloak_user_id}
                      aoSelecionar={() => selecionar(usuario)}
                    />
                  ))}
                </ul>
              ) : (
                <p className="px-3 py-6 text-center text-cell text-on-surface-muted">
                  Nenhum usuário @nasajon.com.br encontrado para "{busca.termo.trim()}".
                </p>
              )}
            </section>
          ) : lista.estado === 'carregando' ? (
            <div className="rounded-input border border-border">
              <LinhasCarregando rotulo="Carregando usuários com capabilities" />
            </div>
          ) : lista.estado === 'erro' ? (
            <EstadoErro erro={lista.erro} aoTentarDeNovo={lista.recarregar} className="py-8" />
          ) : usuariosVisiveis.length > 0 ? (
            <ul
              aria-label="Usuários com capabilities"
              className="cmp-scroll-area max-h-[26rem] divide-y divide-border-subtle overflow-y-auto rounded-input border border-border"
            >
              {usuariosVisiveis.map((usuario) => (
                <LinhaUsuario
                  key={usuario.keycloak_user_id}
                  usuario={usuario}
                  capabilities={usuario.capabilities}
                  selecionado={idSelecionado === usuario.keycloak_user_id}
                  aoSelecionar={() => selecionar(usuario)}
                />
              ))}
            </ul>
          ) : (
            <EstadoVazio
              titulo="Ninguém tem capability ainda"
              descricao="Busque um usuário @nasajon.com.br acima para conceder a primeira capability."
              className="py-8"
            />
          )}
        </CardContent>
      </Card>

      <Card as="aside" aria-label="Capabilities do usuário" className="h-fit lg:sticky lg:top-0">
        <CardHeader>
          <CardTitle>{selecionado ? nomeExibido(selecionado) : 'Capabilities'}</CardTitle>
        </CardHeader>
        <CardContent>
          {!selecionado ? (
            <p className="text-cell text-on-surface-muted">
              Selecione um usuário para ver e alterar suas capabilities.
            </p>
          ) : capsDoUsuario.estado === 'carregando' ? (
            <LinhasCarregando linhas={gerenciaveis.length} rotulo="Carregando capabilities do usuário" />
          ) : capsDoUsuario.estado === 'erro' ? (
            <EstadoErro erro={capsDoUsuario.erro} aoTentarDeNovo={capsDoUsuario.recarregar} className="py-6" />
          ) : (
            <ul className="flex flex-col divide-y divide-border-subtle">
              {gerenciaveis.map((capability) => {
                const concedida = Boolean(capsDoUsuario.dados?.includes(capability.valor));
                const ocupada = emAcao.has(`${selecionado.keycloak_user_id}:${capability.valor}`);
                const barrada = capability.valor === CAPABILITY_SYSADMIN && !ehSysadmin;
                const motivo = 'Só quem já é sysadmin pode conceder ou revogar esta capability.';
                const rotuloId = `cap-${capability.valor}`;
                const alternador = (
                  <Switch
                    aria-labelledby={rotuloId}
                    checked={concedida}
                    disabled={ocupada || barrada}
                    title={barrada ? motivo : undefined}
                    onCheckedChange={() => void alternar(capability.valor, concedida)}
                  />
                );
                return (
                  <li
                    key={capability.valor}
                    className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span id={rotuloId} className="flex flex-wrap items-center gap-1.5 text-cell font-bold">
                        {capability.rotulo}
                        {concedida ? <Badge tone="success">Ativa</Badge> : null}
                      </span>
                      <span className="text-micro font-normal text-on-surface-muted">{capability.descricao}</span>
                    </div>
                    {barrada ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0} aria-label={motivo} className="inline-flex shrink-0 items-center gap-1.5">
                            <Lock className="size-3.5 text-icon" aria-hidden="true" />
                            {alternador}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{motivo}</TooltipContent>
                      </Tooltip>
                    ) : (
                      alternador
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
